/**
 * YuNet ONNX browser implementation.
 * The preprocessing and output decoding are independently implemented from
 * the MIT-licensed OpenCV Zoo YuNet model documentation and ONNX tensors.
 * No W3PN Anonymizer source code is used because its repository license could
 * not be confirmed at the pinned review commit.
 */
import type { DetectionRegion, Rect } from '../types'
import { createId } from '../core/id'
import { dedupeRects, expandRect } from '../core/rect'

const MODEL_SIZE = 640
const STRIDES = [8, 16, 32] as const
const TILE_OVERLAP = 0.24
const DEFAULT_THRESHOLD = 0.53

interface FaceCandidate { rect: Rect; confidence: number }

type OrtModule = typeof import('onnxruntime-web/wasm')
let ortPromise: Promise<OrtModule> | null = null
let sessionPromise: Promise<import('onnxruntime-web/wasm').InferenceSession> | null = null
let inferenceTail: Promise<void> = Promise.resolve()

function assetUrl(path: string): string {
  return new URL(path, document.baseURI).href
}

async function getOrt(): Promise<OrtModule> {
  if (!ortPromise) {
    ortPromise = import('onnxruntime-web/wasm').then((ort) => {
      ort.env.wasm.wasmPaths = assetUrl('./assets/onnx/')
      // Single-thread is the compatibility baseline for iOS Safari and avoids
      // depending on SharedArrayBuffer. The model is intentionally small.
      ort.env.wasm.numThreads = 1
      ort.env.wasm.proxy = false
      return ort
    })
  }
  return ortPromise
}

async function getSession(): Promise<import('onnxruntime-web/wasm').InferenceSession> {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const ort = await getOrt()
      return ort.InferenceSession.create(assetUrl('./assets/models/face_detection_yunet_2023mar.onnx'), {
        executionProviders: ['wasm'],
        graphOptimizationLevel: 'all',
      })
    })().catch((error) => {
      sessionPromise = null
      throw error
    })
  }
  return sessionPromise
}

function sigmoidClamp(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function iou(a: FaceCandidate, b: FaceCandidate): number {
  const x1 = Math.max(a.rect.x, b.rect.x)
  const y1 = Math.max(a.rect.y, b.rect.y)
  const x2 = Math.min(a.rect.x + a.rect.width, b.rect.x + b.rect.width)
  const y2 = Math.min(a.rect.y + a.rect.height, b.rect.y + b.rect.height)
  const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1)
  if (intersection <= 0) return 0
  return intersection / (a.rect.width * a.rect.height + b.rect.width * b.rect.height - intersection)
}

function nms(candidates: FaceCandidate[], threshold = 0.32): FaceCandidate[] {
  const sorted = [...candidates].sort((a, b) => b.confidence - a.confidence)
  const kept: FaceCandidate[] = []
  for (const candidate of sorted) {
    if (!kept.some((existing) => iou(existing, candidate) > threshold)) kept.push(candidate)
  }
  return kept
}

async function inferCanvas(canvas: HTMLCanvasElement, threshold: number): Promise<FaceCandidate[]> {
  const ort = await getOrt()
  const session = await getSession()
  const resizeScale = Math.min(1, MODEL_SIZE / canvas.width, MODEL_SIZE / canvas.height)
  const inputW = Math.max(1, Math.round(canvas.width * resizeScale))
  const inputH = Math.max(1, Math.round(canvas.height * resizeScale))
  const scaleX = canvas.width / inputW
  const scaleY = canvas.height / inputH

  const resized = document.createElement('canvas')
  resized.width = MODEL_SIZE
  resized.height = MODEL_SIZE
  const ctx = resized.getContext('2d', { willReadFrequently: true })
  if (!ctx) return []
  ctx.clearRect(0, 0, MODEL_SIZE, MODEL_SIZE)
  ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, inputW, inputH)
  const pixels = ctx.getImageData(0, 0, MODEL_SIZE, MODEL_SIZE).data
  const plane = MODEL_SIZE * MODEL_SIZE
  const chw = new Float32Array(plane * 3)
  for (let i = 0; i < plane; i += 1) {
    const source = i * 4
    chw[i] = pixels[source + 2] ?? 0
    chw[plane + i] = pixels[source + 1] ?? 0
    chw[plane * 2 + i] = pixels[source] ?? 0
  }

  const input = new ort.Tensor('float32', chw, [1, 3, MODEL_SIZE, MODEL_SIZE])
  const inputName = session.inputNames[0] ?? 'input'
  const results = await session.run({ [inputName]: input })
  const raw: FaceCandidate[] = []

  try {
    for (const stride of STRIDES) {
      const cols = Math.floor(MODEL_SIZE / stride)
      const rows = Math.floor(MODEL_SIZE / stride)
      const cls = results[`cls_${stride}`]?.data as Float32Array | undefined
      const obj = results[`obj_${stride}`]?.data as Float32Array | undefined
      const bbox = results[`bbox_${stride}`]?.data as Float32Array | undefined
      if (!cls || !obj || !bbox) continue
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const index = row * cols + col
          const score = Math.sqrt(sigmoidClamp(cls[index] ?? 0) * sigmoidClamp(obj[index] ?? 0))
          if (score < threshold) continue
          const base = index * 4
          const cx = (col + (bbox[base] ?? 0)) * stride
          const cy = (row + (bbox[base + 1] ?? 0)) * stride
          const width = Math.exp(bbox[base + 2] ?? 0) * stride
          const height = Math.exp(bbox[base + 3] ?? 0) * stride
          const x1 = cx - width / 2
          const y1 = cy - height / 2
          const x2 = x1 + width
          const y2 = y1 + height
          if (x2 <= 0 || y2 <= 0 || x1 >= inputW || y1 >= inputH) continue
          const clippedX1 = Math.max(0, x1)
          const clippedY1 = Math.max(0, y1)
          const clippedX2 = Math.min(inputW, x2)
          const clippedY2 = Math.min(inputH, y2)
          const x = clippedX1 * scaleX
          const y = clippedY1 * scaleY
          raw.push({
            rect: {
              x,
              y,
              width: (clippedX2 - clippedX1) * scaleX,
              height: (clippedY2 - clippedY1) * scaleY,
            },
            confidence: score,
          })
        }
      }
    }
  } finally {
    input.dispose()
    Object.values(results).forEach((tensor) => tensor.dispose())
  }
  return nms(raw)
}

async function inferExclusive(canvas: HTMLCanvasElement, thorough: boolean, onProgress?: (message: string) => void): Promise<FaceCandidate[]> {
  const full = await inferCanvas(canvas, thorough ? 0.46 : DEFAULT_THRESHOLD)
  if (!thorough && Math.max(canvas.width, canvas.height) <= 1200) return full

  const tileSize = 720
  const step = Math.round(tileSize * (1 - TILE_OVERLAP))
  const candidates = [...full]
  const tiles: Array<{ x: number; y: number; width: number; height: number }> = []
  for (let y = 0; y < canvas.height; y += step) {
    for (let x = 0; x < canvas.width; x += step) {
      const width = Math.min(tileSize, canvas.width - x)
      const height = Math.min(tileSize, canvas.height - y)
      if (width >= 96 && height >= 96) tiles.push({ x, y, width, height })
    }
  }

  const tile = document.createElement('canvas')
  const tileCtx = tile.getContext('2d')
  if (!tileCtx) return full
  for (let index = 0; index < tiles.length; index += 1) {
    const area = tiles[index]
    if (!area) continue
    tile.width = area.width
    tile.height = area.height
    tileCtx.clearRect(0, 0, area.width, area.height)
    tileCtx.drawImage(canvas, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height)
    const detected = await inferCanvas(tile, thorough ? 0.42 : 0.48)
    candidates.push(...detected.map((candidate) => ({
      ...candidate,
      rect: { ...candidate.rect, x: candidate.rect.x + area.x, y: candidate.rect.y + area.y },
    })))
    onProgress?.(`顔を詳しく確認中 ${index + 1}/${tiles.length}`)
    if (index % 2 === 1) await new Promise((resolve) => setTimeout(resolve, 0))
  }
  return nms(candidates)
}

export async function detectFaces(
  canvas: HTMLCanvasElement,
  originalWidth: number,
  originalHeight: number,
  analysisScale: number,
  thorough: boolean,
  onProgress?: (message: string) => void,
): Promise<DetectionRegion[]> {
  const run = inferenceTail.then(() => inferExclusive(canvas, thorough, onProgress))
  inferenceTail = run.then(() => undefined, () => undefined)
  const faces = await run
  return dedupeRects(faces.map((face) => {
    const baseRect = {
      x: face.rect.x / analysisScale,
      y: face.rect.y / analysisScale,
      width: face.rect.width / analysisScale,
      height: face.rect.height / analysisScale,
    }
    return {
      id: createId('face'),
      rect: expandRect(baseRect, Math.max(5, baseRect.width * 0.12), originalWidth, originalHeight),
      category: 'face' as const,
      source: 'face' as const,
      label: '顔の候補',
      confidence: face.confidence,
      enabled: true,
    }
  }), 0.45)
}

export async function preloadFaceDetector(): Promise<void> {
  await getSession()
}
